// HoopHero shared styling primitives.
//
// Most exports are static style objects pages spread into `style={...}`.
// A few are now responsive — the desktop value is preserved on the static
// export, and a `useShared()` hook returns the same shape but with values
// scaled for mobile. Pages that import the default object still work
// unchanged on desktop; pages that want responsive tightening can switch
// to the hook with a one-line change.

import { useMemo } from 'react';
import useIsMobile from './hooks/useIsMobile';

// --- Static (desktop-default) shared styles -------------------------------

// Brand orange. ORANGE_INK is the darker shade for anything that carries
// text: filled buttons, links, badges, avatars (5.0:1 on white, 4.5:1 on the
// pale #FFF0E8 tint, WCAG AA). ORANGE stays for decoration only: borders,
// calendar dots, spinners, toggle tracks, checkbox accents, and orange on
// the dark navy landing sections where the bright shade already passes.
export const ORANGE = '#F05A14';
export const ORANGE_INK = '#C2460B';

export const shared = {
  pageHeader: {
    padding: '20px 24px 0',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  pageTitle: {
    fontFamily: 'Georgia, serif',
    fontSize: '25px',
    fontWeight: '800',
    letterSpacing: '0.2px',
    color: '#0D1117',
  },
  pageSubtitle: {
    fontSize: '12.5px',
    color: '#5A6070',
    marginTop: '2px',
  },
  content: { padding: '16px 24px 40px' },
  card: {
    background: '#fff',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: '10px',
    padding: '16px',
  },
  cardTitle: {
    fontFamily: 'Georgia, serif',
    fontSize: '17px',
    fontWeight: '700',
    marginBottom: '12px',
    color: '#0D1117',
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
    gap: '15px',
  },
  grid4: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0,1fr))',
    gap: '11px',
  },
  statCard: {
    background: '#fff',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: '10px',
    padding: '13px 16px',
    borderLeft: `3px solid ${ORANGE}`,
  },
  statLabel: {
    fontSize: '10px',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#9AA0B0',
    fontWeight: '600',
  },
  statValue: {
    fontFamily: 'Georgia, serif',
    fontSize: '28px',
    fontWeight: '800',
    lineHeight: '1',
    marginTop: '3px',
    color: '#0D1117',
  },
  statSub: { fontSize: '11px', marginTop: '4px', color: '#9AA0B0' },
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '7px 14px',
    borderRadius: '7px',
    fontFamily: 'inherit',
    fontSize: '12.5px',
    fontWeight: '600',
    cursor: 'pointer',
    border: 'none',
  },
  btnOrange: { background: ORANGE_INK, color: '#fff' },
  btnBlue: { background: '#1A5FD4', color: '#fff' },
  btnGray: { background: '#ECEEF3', color: '#0D1117' },
  btnGhost: {
    background: 'transparent',
    color: '#5A6070',
    border: '1px solid rgba(0,0,0,0.13)',
  },
  btnGreen: { background: '#E4F5ED', color: '#14855A', border: 'none' },
  btnRed: { background: '#FDEAEA', color: '#C93535', border: 'none' },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '600',
  },
  badgeGreen: { background: '#E4F5ED', color: '#14855A' },
  badgeRed: { background: '#FDEAEA', color: '#C93535' },
  badgeBlue: { background: '#E6EFFC', color: '#1A5FD4' },
  badgeOrange: { background: '#FFF0E8', color: ORANGE_INK },
  badgeGray: { background: '#ECEEF3', color: '#5A6070' },
  input: {
    fontFamily: 'inherit',
    fontSize: '13px',
    padding: '8px 11px',
    border: '1px solid rgba(0,0,0,0.13)',
    borderRadius: '7px',
    background: '#fff',
    color: '#0D1117',
    outline: 'none',
    width: '100%',
    // Without border-box, width:100% adds padding+border ON TOP of the
    // container width, so the input overflows its grid cell by ~24px and
    // the next cell's input visually overlaps. Make `width:100%` actually
    // mean "fit my container."
    boxSizing: 'border-box',
  },
  label: {
    fontSize: '11.5px',
    fontWeight: '600',
    color: '#5A6070',
    letterSpacing: '0.2px',
    display: 'block',
    marginBottom: '5px',
  },
  sep: {
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    margin: '12px 0',
  },
  empty: {
    fontSize: '13px',
    color: '#9AA0B0',
    padding: '20px 0',
    textAlign: 'center',
  },
};

// --- Responsive hook ------------------------------------------------------

/**
 * Same shape as `shared`, but with mobile-tightened values when the
 * viewport is small. Pages opt in by changing:
 *
 *   import shared from '../sharedStyles';
 *
 * to:
 *
 *   import { useShared } from '../sharedStyles';
 *   const shared = useShared();
 *
 * That's the only line they need to change. All keys remain the same.
 */
shared.modalGrid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }; // v2.4 US-3.3

export const useShared = () => {
  const isMobile = useIsMobile();
  return useMemo(() => {
    if (!isMobile) return shared;
    return {
      ...shared,
      pageHeader: {
        ...shared.pageHeader,
        padding: '14px 16px 0',
        gap: '10px',
      },
      pageTitle: {
        ...shared.pageTitle,
        fontSize: '20px',
      },
      content: {
        padding: '12px 16px 28px',
      },
      cardTitle: {
        ...shared.cardTitle,
        fontSize: '15px',
      },
      // Most multi-column grids should stack on mobile. Pages that have
      // a more specific responsive pattern (e.g. 4-col -> 2-col) can still
      // override locally.
      grid2: {
        ...shared.grid2,
        gridTemplateColumns: '1fr',
        gap: '12px',
      },
      grid4: {
        ...shared.grid4,
        gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
        gap: '10px',
      },
      statValue: {
        ...shared.statValue,
        fontSize: '22px',
      },
      modalGrid2: { display: 'grid', gridTemplateColumns: '1fr', gap: '10px' },
    };
  }, [isMobile]);
};

export default shared;
